import { Link } from "wouter";
import { ArrowLeft, RefreshCcw } from "lucide-react";
import { Footer } from "@/components/website/Footer";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/95 border-b border-slate-200 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link href="/" className="flex items-center gap-2.5">
            <img src="/images/finproject-logo.png" alt="FinProject" className="h-8 w-auto" />
            <span className="font-bold text-slate-900 hidden sm:inline">FinProject</span>
          </Link>
          <Link href="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}>
            <RefreshCcw className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Refund Policy</h1>
        </div>
        <p className="text-sm text-slate-500 mb-10">
          Last updated: <strong>27 March 2026</strong> · Effective date: 27 March 2026
        </p>

        <div className="space-y-8">

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <p className="text-slate-700 leading-relaxed">
              This Refund Policy governs subscription cancellations and refund requests for <strong>FinProject</strong>, operated by Business Elite LLC, Shams Free Zone, UAE. By subscribing to any FinProject plan, you agree to this policy.
            </p>
          </div>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Free Trial</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-6 text-slate-600 space-y-3 leading-relaxed">
              <p>
                Most FinProject plans include a <strong>14-day free trial</strong>. You will not be charged during the trial period.
              </p>
              <p>
                If you cancel before the trial ends, you will not be charged. If you do not cancel, your subscription will automatically begin and your payment method will be charged at the end of the trial.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Monthly Subscriptions</h2>
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-6 text-slate-600 space-y-3 leading-relaxed">
                <p>
                  <strong className="text-slate-800">Cancellation:</strong> You may cancel your monthly subscription at any time via your account settings or by contacting <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a>. Cancellation takes effect at the end of the current billing period — you will continue to have full access until then.
                </p>
                <p>
                  <strong className="text-slate-800">Refunds:</strong> Monthly subscription fees are non-refundable once the billing period has started. We do not issue pro-rated refunds for unused days in a billing period.
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                <p className="text-emerald-800 text-sm leading-relaxed">
                  <strong>Exception — First Charge Only:</strong> If you are dissatisfied for any reason after your first paid charge (post-trial), contact us within <strong>7 days</strong> of that charge at <a href="mailto:support@finproject.io" className="text-emerald-700 underline">support@finproject.io</a>. We will review your case and, at our sole discretion, may issue a full refund of that first charge. This exception applies once per customer.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Annual Subscriptions</h2>
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-6 text-slate-600 space-y-3 leading-relaxed">
                <p>
                  <strong className="text-slate-800">Cancellation:</strong> You may cancel your annual subscription at any time. Cancellation stops the subscription from auto-renewing at the next anniversary; you retain access until the end of the paid annual term.
                </p>
                <p>
                  <strong className="text-slate-800">Refunds within 14 days:</strong> If you cancel an annual subscription within <strong>14 calendar days</strong> of the annual billing date and have not substantially used the Service during that period (as reasonably determined by us based on platform activity), you are eligible for a full refund of the annual fee.
                </p>
                <p>
                  <strong className="text-slate-800">Refunds after 14 days:</strong> Annual subscription fees are non-refundable after the 14-day window. No pro-rated refunds are issued for unused months.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Upgrades & Downgrades</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-6 text-slate-600 space-y-3 leading-relaxed">
              <p>
                <strong className="text-slate-800">Upgrading:</strong> When you upgrade to a higher plan, the new rate takes effect immediately. You will be charged a prorated amount for the remainder of the current billing period, then the full new rate on your next billing date.
              </p>
              <p>
                <strong className="text-slate-800">Downgrading:</strong> When you downgrade, the lower rate takes effect at the start of your next billing period. No refund is issued for the difference in the current period.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Account Termination by FinProject</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-6 text-slate-600 leading-relaxed">
              <p>
                If FinProject terminates your account due to a material breach of our <Link href="/terms" className="text-blue-600 hover:underline">Terms of Service</Link> (e.g., misuse, fraud, or illegal activity), no refund will be issued for any unused portion of your subscription.
              </p>
              <p className="mt-3">
                If FinProject discontinues the Service entirely, we will provide at least 60 days' notice and issue pro-rated refunds for any remaining prepaid periods.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4">How Refunds Are Processed</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-slate-700 space-y-4 leading-relaxed">
              <p>
                All refunds are initiated through <strong>Stripe</strong>, our payment processor, and returned to your <strong>original payment method</strong> (the card or bank account used for the initial charge). FinProject cannot issue refunds to a different card, bank account, or payment method.
              </p>
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Processing Time</h3>
                <p className="text-sm">
                  Once a refund is approved, it is submitted to Stripe immediately. Refunds typically appear on your statement within <strong>5–10 business days</strong>, depending on your card issuer or bank. In some cases, it may take up to one billing cycle. Stripe will send you a confirmation email when the refund is processed.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Annual Plan Partial Refunds</h3>
                <p className="text-sm">
                  Where a partial refund is applicable for an annual subscription (e.g., service discontinuation by FinProject), the refund amount is calculated proportionally: the unused full months remaining in your annual term, divided by 12, multiplied by the annual fee paid. Partial months are not included in the calculation.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Currency</h3>
                <p className="text-sm">
                  Refunds are issued in the original currency of the transaction (USD). Any exchange rate differences between the original charge date and the refund date are determined by your bank or card issuer, not by FinProject or Stripe.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4">How to Request a Refund</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-6 text-slate-600 space-y-3 leading-relaxed">
              <p>To request a refund, contact us at <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a> with:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Your registered email address</li>
                <li>Your company name</li>
                <li>The invoice number or Stripe receipt</li>
                <li>The reason for your request</li>
              </ul>
              <p className="text-sm">We process refund requests within <strong>5–10 business days</strong>. Approved refunds are returned to the original payment method. Processing time by your bank or card issuer may add additional days.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Summary Table</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Situation</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Refund</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    ["Cancel during free trial", "✅ No charge"],
                    ["Cancel monthly plan mid-period", "❌ No refund for unused days"],
                    ["First monthly charge — cancel within 7 days", "✅ Eligible for full refund (first charge, once per customer)"],
                    ["Cancel annual plan within 14 days of charge (low usage)", "✅ Full annual refund"],
                    ["Cancel annual plan after 14 days", "❌ No refund; access continues until year end"],
                    ["Upgrade plan", "✅ Prorated credit applied immediately"],
                    ["Downgrade plan", "❌ No refund; lower rate starts next cycle"],
                    ["Account terminated for ToS violation", "❌ No refund"],
                    ["FinProject discontinues service", "✅ Pro-rated refund for remaining prepaid period"],
                  ].map(([situation, refund]) => (
                    <tr key={situation} className="bg-white">
                      <td className="px-4 py-3 text-slate-600">{situation}</td>
                      <td className="px-4 py-3 text-slate-700 font-medium">{refund}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Contact</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="text-slate-600 space-y-1 text-sm">
                <p><strong>Business Elite LLC</strong> — Shams Free Zone, Sharjah, UAE</p>
                <p>Email: <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a></p>
              </div>
            </div>
          </section>

        </div>
      </div>

      <Footer />
    </div>
  );
}
